import React from 'react';

import { ExpandMore } from '@mui/icons-material';
import FlightIcon from '@mui/icons-material/Flight';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import PaymentsIcon from '@mui/icons-material/Payments';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  GridLegacy as Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { getBundleStatusInfo } from '~/components/Accommodation/utils/rates';

import { useGetPropertyVendorAccountId } from '~/hooks/apis/accommodation/useGetPropertyVendorAccountId';

import { MergedRate, Rate } from '~/services/AccommodationService';

import MetaInfoItem from './MetaInfoItem';
import OriginalPrice from './OriginalPrice';
import { formatPrice } from './utils/formatPrice';
import {
  LuxPlusOutcome,
  describeLuxPlusWithdrawal,
  describeMarginUplift,
  describePriceMatch,
  formatMarginChange,
  getPriceMatchDetails,
} from './utils/priceMatch';

interface RateDetailsModalProps {
  rate: Rate;
  currency: string;
  /**
   * The rates this one was picked over, when the caller has them. Only used to report what the
   * rate a price match displaced would have earned — the match is rendered without it otherwise.
   */
  mergedRates?: Array<MergedRate>;
}

const LUX_PLUS_OUTCOME_DESCRIPTIONS: Record<LuxPlusOutcome, string> = {
  unchanged:
    'Unchanged — a partner-funded member price does not derive from the public sell, so the match did not move it.',
  repriced: "Repriced — re-derived from the supplier's own rule at the matched public price.",
  tracked:
    'Tracked — the rate carried no member discount, only the member value of its LuxPlus+ inclusions, so the member price moved with the public one.',
  withdrawn: 'Withdrawn — the rate ended up with no member price at the matched public price.',
};

type ReservationRateMeta = Extract<NonNullable<Rate['meta']>, { type: 'reservation' }>;
type ReservationRateMetaWithOptionalVendor = ReservationRateMeta & {
  vendorId?: string;
  vendorAccountId?: string;
  vendor_account_id?: string;
};

const toNonEmptyString = (value: unknown): string => {
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return '';
};

const formatMetaKey = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

function isReservationRateMeta(meta: Rate['meta']): meta is ReservationRateMeta {
  return (
    !!meta &&
    meta.type === 'reservation' &&
    'reservationPropertyId' in meta &&
    'reservationRoomTypeId' in meta &&
    'reservationRoomRateId' in meta
  );
}

function getVendorIdFromMeta(meta: ReservationRateMeta | undefined): string {
  const reservationMetaWithOptionalVendor = meta as ReservationRateMetaWithOptionalVendor | undefined;
  return toNonEmptyString(
    reservationMetaWithOptionalVendor?.vendorId ??
      reservationMetaWithOptionalVendor?.vendorAccountId ??
      reservationMetaWithOptionalVendor?.vendor_account_id,
  );
}

export default function RateDetailsModal({ rate, currency, mergedRates = [] }: RateDetailsModalProps) {
  const theme = useTheme();
  // HP-2812: a matched rate is selling below what it was priced at, funded out of its own margin.
  // Its prices on their own no longer explain why it won its group, so the modal has to say what
  // moved and what the trade earned.
  const priceMatch = getPriceMatchDetails(rate, { mergedRates });
  const marginChange = priceMatch?.marginChange && formatMarginChange(priceMatch.marginChange);
  const marginUplift = priceMatch && describeMarginUplift(priceMatch);
  const hasPayableAtProperty = rate.propertyFees && rate.propertyFees.length > 0;
  const reservationMeta = isReservationRateMeta(rate.meta) ? rate.meta : undefined;
  const isLpcRate = !!reservationMeta;

  const reservationPropertyId = toNonEmptyString(reservationMeta?.reservationPropertyId);
  const reservationRoomTypeId = toNonEmptyString(reservationMeta?.reservationRoomTypeId);
  const reservationRoomRateId = toNonEmptyString(reservationMeta?.reservationRoomRateId);
  const vendorIdFromMeta = getVendorIdFromMeta(reservationMeta);
  const {
    data: vendorIdFromProperty,
    isLoading: isVendorIdLoading,
    isError: isVendorIdError,
  } = useGetPropertyVendorAccountId({
    propertyId: reservationPropertyId,
    enabled: isLpcRate && !!reservationPropertyId && !vendorIdFromMeta,
  });
  const resolvedVendorId = vendorIdFromMeta || vendorIdFromProperty || '';
  const isResolvingVendorId = isLpcRate && !!reservationPropertyId && !vendorIdFromMeta && isVendorIdLoading;

  const propertyLink =
    resolvedVendorId && reservationPropertyId ? `/vendors/${resolvedVendorId}/properties/${reservationPropertyId}` : '';
  const roomRateLink =
    resolvedVendorId && reservationPropertyId && reservationRoomTypeId && reservationRoomRateId
      ? `/vendors/${resolvedVendorId}/properties/${reservationPropertyId}/room-types/${reservationRoomTypeId}/room-rates/${reservationRoomRateId}`
      : '';
  const bundleStatusInfo = getBundleStatusInfo(rate.meta);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Rate Details:
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6">Pricing Information</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Selling Price:
                </Typography>{' '}
                {formatPrice(rate.total.sellingPrice, currency)}
                {priceMatch?.originalSellingPrice != null && (
                  <OriginalPrice price={priceMatch.originalSellingPrice} currency={currency} />
                )}
              </Typography>
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Display Price:
                </Typography>{' '}
                {formatPrice(rate.total.displayPrice, currency)}
                {priceMatch?.originalDisplayPrice != null && (
                  <OriginalPrice price={priceMatch.originalDisplayPrice} currency={currency} />
                )}
              </Typography>
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Before Taxes/Fees:
                </Typography>{' '}
                {formatPrice(rate.total.beforeTaxesFeesPrice, currency)}
              </Typography>
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Taxes and Fees:
                </Typography>{' '}
                {formatPrice(rate.total.taxesAndFees, currency)}
              </Typography>
              {hasPayableAtProperty && (
                <Typography sx={{ color: theme.palette.warning.dark, fontWeight: 'bold', mt: 1 }}>
                  <PaymentsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Pay at Property: {formatPrice(rate.total.propertyFees, currency)}
                </Typography>
              )}
              <Box sx={{ mt: 1, p: 1, bgcolor: theme.palette.grey[100], borderRadius: 1 }}>
                <Typography variant="subtitle2">Total Cost:</Typography>
                <Typography variant="h6" color="primary">
                  {formatPrice(rate.total.displayPrice, currency)}
                </Typography>
                {rate.total.discount > 0 && (
                  <Typography color="secondary" sx={{ fontWeight: 'bold', mt: 1 }}>
                    <LoyaltyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Discount: {(rate.total.discount * 100).toFixed(0)}% off
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} sx={{ mb: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h6">Rate Information</Typography>
              <Divider sx={{ my: 1 }} />
              {rate.name && (
                <Typography>
                  <Typography component="span" fontWeight="bold">
                    Rate Name:
                  </Typography>{' '}
                  {rate.name}
                </Typography>
              )}
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Rate ID:
                </Typography>{' '}
                {rate.id}
              </Typography>
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Rate Group Key:
                </Typography>{' '}
                {rate.groupKey}
              </Typography>
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Board Code:
                </Typography>{' '}
                {rate.boardCode}
              </Typography>
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Supplier:
                </Typography>{' '}
                {rate.meta && 'supplier' in rate.meta ? rate.meta.supplier : 'N/A'}
              </Typography>
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Packaged:
                </Typography>{' '}
                {bundleStatusInfo?.summary ?? 'No'}
              </Typography>
              {bundleStatusInfo && (
                <Box sx={{ mt: 1, p: 1, bgcolor: theme.palette[bundleStatusInfo.color].light, borderRadius: 1 }}>
                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                    <FlightIcon sx={{ mr: 1 }} />
                    {bundleStatusInfo.description}
                  </Typography>
                </Box>
              )}
              {rate.meta && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={JSON.stringify(rate.meta).toLowerCase().includes('bedbank') ? 'Bedbank' : 'LPC'}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        {priceMatch && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6">Dynamic Price Match</Typography>
                  <Chip size="small" color="warning" label="Price matched" />
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Typography>{describePriceMatch(priceMatch, currency)}</Typography>
                {priceMatch.discount != null && (
                  <Typography sx={{ mt: 1 }}>
                    <Typography component="span" fontWeight="bold">
                      Discount:
                    </Typography>{' '}
                    {formatPrice(priceMatch.discount, currency)}
                    {priceMatch.discountAud != null && currency !== 'AUD' && (
                      <Typography component="span" color="text.secondary">
                        {` (${formatPrice(priceMatch.discountAud, 'AUD')})`}
                      </Typography>
                    )}
                  </Typography>
                )}
                {marginChange && (
                  <Typography>
                    <Typography component="span" fontWeight="bold">
                      Margin:
                    </Typography>{' '}
                    {marginChange.dollars}
                    {marginChange.percent != null && (
                      <Typography component="span" color="text.secondary">
                        {` (${marginChange.percent})`}
                      </Typography>
                    )}
                  </Typography>
                )}
                {priceMatch.luxPlus.outcome && (
                  <Typography>
                    <Typography component="span" fontWeight="bold">
                      LuxPlus+ price:
                    </Typography>{' '}
                    {priceMatch.luxPlus.withdrawn ? 'Withdrawn' : formatPrice(rate.luxPlus?.displayPrice, currency)}
                    {priceMatch.luxPlus.originalDisplayPrice != null && (
                      <OriginalPrice price={priceMatch.luxPlus.originalDisplayPrice} currency={currency} />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {describeLuxPlusWithdrawal(priceMatch) ??
                        LUX_PLUS_OUTCOME_DESCRIPTIONS[priceMatch.luxPlus.outcome]}
                    </Typography>
                  </Typography>
                )}
                {priceMatch.adjustment.matchedToRateId && (
                  <Typography>
                    <Typography component="span" fontWeight="bold">
                      Matched to rate:
                    </Typography>{' '}
                    {priceMatch.adjustment.matchedToRateId}
                  </Typography>
                )}
                {marginUplift != null && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: theme.palette.grey[100], borderRadius: 1 }}>
                    <Typography>{marginUplift}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        <Card sx={{ marginLeft: 2, marginTop: 0 }}>
          {rate.meta && (
            <>
              {isLpcRate && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Links</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {isResolvingVendorId ? (
                      <Typography>Vendor ID: Resolving...</Typography>
                    ) : isVendorIdError || !resolvedVendorId ? (
                      <Typography>Vendor ID unavailable</Typography>
                    ) : (
                      <>
                        {propertyLink ? (
                          <MetaInfoItem label="Property" value={propertyLink} url={propertyLink} />
                        ) : (
                          <Typography>Property: Not available</Typography>
                        )}
                        {roomRateLink ? (
                          <MetaInfoItem label="Room rate" value={roomRateLink} url={roomRateLink} />
                        ) : (
                          <Typography>Room rate: Not available</Typography>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Additional meta data</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Object.entries(rate.meta)
                    .filter(([key]) => !['group', 'facilities', 'promotions', 'bedGroups'].includes(key))
                    .map(([key, value]) => {
                      if (typeof value === 'string' || typeof value === 'number') {
                        const stringValue = value.toString();
                        const url = key === 'offerId' ? `/offers/${stringValue}` : undefined;
                        return <MetaInfoItem key={key} label={formatMetaKey(key)} value={stringValue} url={url} />;
                      }
                      return null;
                    })}
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Card>
      </Grid>
    </Box>
  );
}
